import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import './App.css';
import './DarkMode.css';

// Pages — EAGER load (first paint critical: public landing, login, public payment pages)
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import PublicPaymentPage from './pages/PublicPaymentPage';
import PublicInvoicePage from './pages/PublicInvoicePage';
import PaymentResultPage from './pages/PaymentResultPage';
import UsdaCheckoutPage from './pages/UsdaCheckoutPage';
import PublicSubscriptionPage from './pages/PublicSubscriptionPage';
import DonationPage from './pages/DonationPage';

// PERF 2026-06-23: LAZY load delle route protette (admin/merchant/agent dashboards).
// Riduce il bundle iniziale di ~70% — first paint molto più veloce su mobile 4G.
// Le pagine sono caricate solo quando l'utente naviga lì.
const MerchantDashboard = lazy(() => import('./pages/MerchantDashboard'));
const PaymentLinksPage = lazy(() => import('./pages/PaymentLinksPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const EmailTransactionSearchPage = lazy(() => import('./pages/EmailTransactionSearchPage'));
const APIKeysPage = lazy(() => import('./pages/APIKeysPage'));
const APIDocsPage = lazy(() => import('./pages/APIDocsPage'));
const APIIntegrationDocsPage = lazy(() => import('./pages/APIIntegrationDocsPage'));
const WordPressDocsPage = lazy(() => import('./pages/WordPressDocsPage'));
const WordPressIntegrationPage = lazy(() => import('./pages/WordPressIntegrationPage'));
const BrandingPage = lazy(() => import('./pages/BrandingPage'));
const WithdrawalsPage = lazy(() => import('./pages/WithdrawalsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const DatabaseMonitoringPro = lazy(() => import('./pages/DatabaseMonitoringPro'));
const DatabaseControlCenter = lazy(() => import('./pages/DatabaseControlCenter'));
const PayoutTimingPage = lazy(() => import('./pages/PayoutTimingPage'));
const AgentDashboard = lazy(() => import('./pages/AgentDashboard'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const HolaPayAdminPage = lazy(() => import('./pages/HolaPayAdminPage'));
const UsdaPaymentReviewPage = lazy(() => import('./pages/UsdaPaymentReviewPage'));
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage'));
const TerminalsPage = lazy(() => import('./pages/TerminalsPage'));
const MerchantDonationsPage = lazy(() => import('./pages/MerchantDonationsPage'));
const CryptoPayoutDashboard = lazy(() => import('./pages/CryptoPayoutDashboard'));
const MerchantCryptoSettings = lazy(() => import('./pages/MerchantCryptoSettings'));
const AgentCryptoSettings = lazy(() => import('./pages/AgentCryptoSettings'));
const RefundsPage = lazy(() => import('./pages/RefundsPage'));
const RoxPayPayoutPage = lazy(() => import('./pages/RoxPayPayoutPage'));
const RoxPayMethodsPage = lazy(() => import('./pages/RoxPayMethodsPage'));

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if must change password
  if (user.must_change_password && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password?first=true" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'super_admin') return <Navigate to="/admin" replace />;
    if (user.role === 'agent') return <Navigate to="/agent" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    // Check if must change password first
    if (user.must_change_password) {
      return <Navigate to="/change-password?first=true" replace />;
    }
    if (user.role === 'super_admin') return <Navigate to="/admin" replace />;
    if (user.role === 'agent') return <Navigate to="/agent" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  // PERF 2026-06-23: Suspense fallback for lazy-loaded pages.
  // Shows a centered spinner while a route chunk is being fetched on first navigation.
  const SuspenseFallback = (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
  return (
    <Suspense fallback={SuspenseFallback}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/docs" element={<APIDocsPage />} />
      <Route path="/api-docs" element={<APIDocsPage />} />
      <Route path="/docs/integration" element={<APIIntegrationDocsPage />} />
      <Route path="/docs/wordpress" element={<WordPressDocsPage />} />
      
      {/* Public Payment/Invoice Pages */}
      <Route path="/pay/:linkId" element={<PublicPaymentPage />} />
      <Route path="/pay/success" element={<PaymentResultPage type="success" />} />
      <Route path="/pay/failed" element={<PaymentResultPage type="failed" />} />
      <Route path="/pay/cancel" element={<PaymentResultPage type="cancel" />} />
      <Route path="/pay/usda/:orderId" element={<UsdaCheckoutPage />} />
      <Route path="/invoice/:invoiceId" element={<PublicInvoicePage />} />
      
      {/* Public Subscription Pages */}
      <Route path="/subscribe/:planId" element={<PublicSubscriptionPage />} />
      <Route path="/subscribe/:planId/success" element={<PublicSubscriptionPage />} />
      <Route path="/subscribe/:planId/cancel" element={<PublicSubscriptionPage />} />
      
      {/* Public Donation Page */}
      <Route path="/donate/:causeId" element={<DonationPage />} />
      
      {/* Merchant Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <MerchantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/payment-links" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <PaymentLinksPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/subscriptions" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <SubscriptionsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/invoices" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <InvoicesPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/transactions" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <TransactionsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/email-transaction-search" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <EmailTransactionSearchPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/donations" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <MerchantDonationsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/api-keys" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <APIKeysPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/wordpress" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <WordPressIntegrationPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/branding" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <BrandingPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/withdrawals" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <WithdrawalsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/settings" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/terminals" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <TerminalsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/refunds" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <RefundsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/crypto-payout" element={
        <ProtectedRoute allowedRoles={['merchant']}>
          <MerchantCryptoSettings />
        </ProtectedRoute>
      } />

      {/* Agent Routes */}
      <Route path="/agent" element={
        <ProtectedRoute allowedRoles={['agent']}>
          <AgentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/agent/crypto-payout" element={
        <ProtectedRoute allowedRoles={['agent']}>
          <AgentCryptoSettings />
        </ProtectedRoute>
      } />
      <Route path="/agent/*" element={
        <ProtectedRoute allowedRoles={['agent']}>
          <AgentDashboard />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/crypto-payout" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <CryptoPayoutDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/refunds" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <RefundsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/roxpay-payout" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <RoxPayPayoutPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/roxpay-methods" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <RoxPayMethodsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/holapay" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <HolaPayAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/usda/payment-reviews" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <UsdaPaymentReviewPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/alphapay" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <HolaPayAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/db-monitor" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <DatabaseMonitoringPro />
        </ProtectedRoute>
      } />
      <Route path="/admin/db-control" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <DatabaseControlCenter />
        </ProtectedRoute>
      } />
      <Route path="/admin/payout-timing" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <PayoutTimingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
